import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/ui-bits";
import { Mail, Reply, UserPlus, Check, Send, Sparkles, Users, Building2, Handshake, ArrowUpRight, CheckCircle2 } from "lucide-react";

const iconMap = { mail: Mail, reply: Reply, "user-plus": UserPlus, check: Check, send: Send, sparkles: Sparkles };

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white p-4 hover:shadow-sm transition-shadow">
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent ? "bg-amber-50" : "brand-bg-soft"}`}>
        <Icon className={`h-[17px] w-[17px] ${accent ? "text-amber-600" : "text-[hsl(245,50%,48%)]"}`} />
      </div>
      <div className="mt-3 text-[26px] font-semibold leading-none tabular">{value}</div>
      <div className="mt-1.5 text-[13px] text-[hsl(240,4%,48%)]">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.stats(), api.sequences(), api.activity()])
      .then(([s, seq, act]) => { setStats(s); setSequences(seq); setActivity(act); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  const activeSeq = sequences.filter((s) => s.status === "Active");
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="p-6 max-w-[1200px] mx-auto animate-in-up">
      <div className="mb-5">
        <h1 className="text-[22px] font-semibold">{greeting}, {user?.name?.split(" ")[0]}</h1>
        <p className="text-[14px] text-[hsl(240,4%,48%)] mt-0.5">Hermes has been busy. Here's what needs your attention.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3.5">
        <StatCard icon={Users} label="People" value={stats.people} />
        <StatCard icon={Building2} label="Companies" value={stats.companies} />
        <StatCard icon={Handshake} label="Open deals" value={stats.open_deals} />
        <StatCard icon={Send} label="Enrolled" value={stats.enrolled} />
        <StatCard icon={CheckCircle2} label="Awaiting approval" value={stats.pending_approvals} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-xl border border-[hsl(240,6%,91%)] bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(240,6%,92%)]">
            <h2 className="text-[14px] font-semibold">Active sequences</h2>
            <button onClick={() => navigate("/sequences")} className="inline-flex items-center gap-1 text-[13px] text-[hsl(245,50%,50%)] font-medium hover:underline">View all <ArrowUpRight className="h-3.5 w-3.5" /></button>
          </div>
          <div className="divide-y divide-[hsl(240,6%,94%)]">
            {activeSeq.length === 0 && <div className="px-4 py-8 text-[13px] text-[hsl(240,4%,55%)]">No active sequences yet.</div>}
            {activeSeq.map((s) => (
              <button key={s.id} onClick={() => navigate(`/sequences/${s.id}`)} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-[hsl(240,20%,99%)] transition-colors text-left">
                <div className="h-9 w-9 rounded-lg brand-bg-soft flex items-center justify-center shrink-0"><Send className="h-4 w-4 text-[hsl(245,50%,48%)]" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-medium truncate">{s.name}</div>
                  <div className="text-[12px] text-[hsl(240,4%,50%)]">{s.enrolled} enrolled · {s.active} active · {s.replied} replied</div>
                </div>
                {s.pending_approval > 0 && (
                  <span className="shrink-0 inline-flex items-center gap-1 rounded-md bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[11px] font-medium">{s.pending_approval} to review</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white">
          <div className="px-4 py-3 border-b border-[hsl(240,6%,92%)]"><h2 className="text-[14px] font-semibold">Recent activity</h2></div>
          <div className="p-2">
            {activity.slice(0, 7).map((a) => {
              const Icon = iconMap[a.icon] || Mail;
              return (
                <div key={a.id} className="flex items-start gap-3 px-2 py-2.5 rounded-lg hover:bg-[hsl(240,20%,99%)]">
                  <div className="h-7 w-7 rounded-full bg-[hsl(240,5%,95%)] flex items-center justify-center shrink-0 mt-0.5"><Icon className="h-3.5 w-3.5 text-[hsl(240,4%,45%)]" /></div>
                  <p className="text-[13px] leading-snug">{a.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
